import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, Activity, TrendingUp, AlertTriangle, Cpu, HardDrive, Wifi, Clock } from 'lucide-react';
import { apiClient } from '../../services/api-client';

interface SystemMetrics {
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  network_throughput: number;
  response_time: number;
  requests_per_minute: number;
  error_rate: number;
}

interface MonitoringHealth {
  status: string;
  services: {
    prometheus: string;
    grafana: string;
    alerting: string;
  };
  last_check: string;
}

interface TimeSeriesMetric {
  timestamp: string;
  value: number;
  metric_type: string;
}

interface MonitoringProps {
  className?: string;
}

export const Monitoring: React.FC<MonitoringProps> = ({ className }) => {
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [monitoringHealth, setMonitoringHealth] = useState<MonitoringHealth | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string>('requests');

  // Load monitoring data
  const loadMonitoringData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all monitoring data in parallel
      const [metricsResponse, healthResponse, timeSeriesResponse] = await Promise.allSettled([
        apiClient.get<{ success: boolean; data: SystemMetrics }>('/api/v1/monitoring/metrics'),
        apiClient.get<{ success: boolean; data: MonitoringHealth }>('/api/v1/monitoring/health'),
        apiClient.get<TimeSeriesMetric[]>(`/api/v1/monitoring/metrics?metric_type=${selectedMetric}`),
      ]);

      // Process metrics
      if (metricsResponse.status === 'fulfilled' && metricsResponse.value.data) {
        setSystemMetrics(metricsResponse.value.data);
      }

      // Process health
      if (healthResponse.status === 'fulfilled' && healthResponse.value.data) {
        setMonitoringHealth(healthResponse.value.data);
      }

      // Process time series data
      if (timeSeriesResponse.status === 'fulfilled') {
        setTimeSeriesData(timeSeriesResponse.value);
      }

    } catch (err) {
      setError('Failed to load monitoring data');
      console.error('Error loading monitoring data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load time series data for specific metric
  const loadTimeSeriesData = async (metricType: string) => {
    try {
      const response = await apiClient.get<TimeSeriesMetric[]>(`/api/v1/monitoring/metrics?metric_type=${metricType}`);
      setTimeSeriesData(response);
    } catch (err) {
      console.error('Error loading time series data:', err);
    }
  };

  useEffect(() => {
    loadMonitoringData();
    const interval = setInterval(loadMonitoringData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadTimeSeriesData(selectedMetric);
  }, [selectedMetric]);

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-500';
    
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'operational':
        return 'bg-green-500';
      case 'degraded':
      case 'warning':
        return 'bg-yellow-500';
      case 'critical':
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getUsageColor = (usage: number) => {
    if (usage < 50) return 'text-green-600';
    if (usage < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatMetricValue = (value: number, type: string) => {
    switch (type) {
      case 'cpu_usage':
      case 'memory_usage':
      case 'disk_usage':
        return `${value.toFixed(1)}%`;
      case 'network_throughput':
        return `${value.toFixed(1)} MB/s`;
      case 'response_time':
        return `${(value * 1000).toFixed(0)}ms`;
      case 'requests_per_minute':
        return `${value.toFixed(0)}/min`;
      case 'error_rate':
        return `${value.toFixed(2)}%`;
      default:
        return value.toString();
    }
  };

  if (loading) {
    return (
      <div className={`monitoring-content ${className}`}>
        <div className="loading-state">
          <div className="flex items-center justify-center h-32" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 loading-spinner" aria-label="Loading monitoring data"></div>
            <p className="ml-3">Loading monitoring data...</p>
            <span className="sr-only">Loading monitoring data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`monitoring-content ${className}`}>
        <div className="error-state">
          <div className="text-red-500 text-center" role="alert" aria-live="assertive">{error}</div>
          <Button onClick={loadMonitoringData} className="mt-2">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`monitoring-content ${className}`}>
      {/* Header with actions */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Monitoring</h2>
          <p className="text-gray-600">Real-time system performance and metrics</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadMonitoringData}
            className="flex items-center gap-2"
            aria-label="Refresh monitoring data"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Health Overview */}
      {monitoringHealth && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" aria-hidden="true" />
              Monitoring System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">Overall</div>
                <Badge className={`${getStatusColor(monitoringHealth.status)} text-white mt-1`}>
                  {monitoringHealth.status}
                </Badge>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">Prometheus</div>
                <Badge className={`${getStatusColor(monitoringHealth.services.prometheus)} text-white mt-1`}>
                  {monitoringHealth.services.prometheus}
                </Badge>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">Grafana</div>
                <Badge className={`${getStatusColor(monitoringHealth.services.grafana)} text-white mt-1`}>
                  {monitoringHealth.services.grafana}
                </Badge>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">Alerting</div>
                <Badge className={`${getStatusColor(monitoringHealth.services.alerting)} text-white mt-1`}>
                  {monitoringHealth.services.alerting}
                </Badge>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-600 text-center">
              Last check: {new Date(monitoringHealth.last_check).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Metrics Grid */}
      {systemMetrics && (
        <div className="monitoring-metrics-grid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">CPU Usage</p>
                    <p className={`text-2xl font-bold ${getUsageColor(systemMetrics.cpu_usage)}`}>
                      {formatMetricValue(systemMetrics.cpu_usage, 'cpu_usage')}
                    </p>
                  </div>
                  <Cpu className="h-8 w-8 text-blue-500" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Memory Usage</p>
                    <p className={`text-2xl font-bold ${getUsageColor(systemMetrics.memory_usage)}`}>
                      {formatMetricValue(systemMetrics.memory_usage, 'memory_usage')}
                    </p>
                  </div>
                  <HardDrive className="h-8 w-8 text-green-500" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Network</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatMetricValue(systemMetrics.network_throughput, 'network_throughput')}
                    </p>
                  </div>
                  <Wifi className="h-8 w-8 text-purple-500" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Response Time</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatMetricValue(systemMetrics.response_time, 'response_time')}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-500" aria-hidden="true" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Requests/min</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatMetricValue(systemMetrics.requests_per_minute, 'requests_per_minute')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Error Rate</p>
                  <p className={`text-2xl font-bold ${getUsageColor(systemMetrics.error_rate)}`}>
                    {formatMetricValue(systemMetrics.error_rate, 'error_rate')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Disk Usage</p>
                  <p className={`text-2xl font-bold ${getUsageColor(systemMetrics.disk_usage)}`}>
                    {formatMetricValue(systemMetrics.disk_usage, 'disk_usage')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Time Series Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" aria-hidden="true" />
            Performance Trends
          </CardTitle>
          <div className="flex gap-2 mt-2">
            {['requests', 'response_time', 'error_rate'].map((metric) => (
              <Button
                key={metric}
                variant={selectedMetric === metric ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setSelectedMetric(metric)}
              >
                {metric.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {timeSeriesData.length > 0 ? (
            <div className="space-y-2">
              {timeSeriesData.slice(-10).map((data, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">
                    {new Date(data.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="font-medium">
                    {formatMetricValue(data.value, data.metric_type)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No time series data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            System Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {systemMetrics && systemMetrics.cpu_usage > 80 && (
              <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">High CPU usage detected</span>
              </div>
            )}
            {systemMetrics && systemMetrics.memory_usage > 85 && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">Critical memory usage</span>
              </div>
            )}
            {systemMetrics && systemMetrics.error_rate > 5 && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">High error rate detected</span>
              </div>
            )}
            {(!systemMetrics || 
              (systemMetrics.cpu_usage <= 80 && 
               systemMetrics.memory_usage <= 85 && 
               systemMetrics.error_rate <= 5)) && (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                <Activity className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-800">All systems operating normally</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 